import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query'
import {
  ListTableReservationTypes,
  ListTableReservationTypesRequest,
  TableReservationTypesApi,
} from '@trybeapp/sdk'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { apiConfiguration } from 'utilities/TrybeApiClient'
import { usePageQueryParam } from 'hooks/UsePageQueryParam'

const api = new TableReservationTypesApi(apiConfiguration)

export const useTableReservationTypes = (
  params: ListTableReservationTypesRequest,
  options?: UseQueryOptions<ListTableReservationTypes>
) => {
  const siteId = useCurrentSiteId()
  const [page] = usePageQueryParam()

  return useQuery<ListTableReservationTypes>(
    ['tableReservationTypes', params],
    () => api.listTableReservationTypes({ ...params, page, siteId: siteId }),
    options
  )
}

export const useInfiniteTableReservationTypes = (params: any = {}) => {
  const siteId = useCurrentSiteId()
  params.siteId = params.siteId ?? siteId

  return useInfiniteQuery(
    ['inifiniteTableReservationTypes', { params }],
    useFetchTableReservationTypes,
    {
      getNextPageParam: (lastPage: any) => {
        return lastPage.nextPage
      },
    }
  )
}

const useFetchTableReservationTypes = async ({ queryKey, pageParam: page = 1 }) => {
  const siteId = useCurrentSiteId()
  const response = await api.listTableReservationTypes({ siteId, page })

  return {
    data: response.data,
    page,
    nextPage: response.meta.last_page > page ? page + 1 : undefined,
    total: response.meta.total,
  }
}

export const useGetTableReservationType = (tableReservationTypeId: string, options = {}) =>
  useQuery(
    ['tableReservationType', { tableReservationTypeId }],
    () => api.getTableReservationType({ tableReservationTypeId }),
    options
  )

export const useCreateTableReservationType = () => {
  const queryClient = useQueryClient()
  const siteId = useCurrentSiteId()

  return useMutation(
    (tableReservationType: any) =>
      api.createTableReservationType({
        createTableReservationTypeRequest: {
          ...tableReservationType,
          site_id: siteId,
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tableReservationTypes'])
      },
    }
  )
}

export const useUpdateTableReservationType = (tableReservationTypeId: string) => {
  const queryClient = useQueryClient()

  return useMutation<unknown, unknown, any>(
    (tableReservationType) =>
      api.updateTableReservationType({
        tableReservationTypeId,
        updateTableReservationTypeRequest: tableReservationType,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tableReservationTypes'])
        queryClient.invalidateQueries(['tableReservationType', { tableReservationTypeId }])
      },
    }
  )
}

export const useArchiveTableReservationType = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (tableReservationTypeId: string) => {
      return api.deleteTableReservationType({ tableReservationTypeId })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tableReservationTypes'])
      },
    }
  )
}

export const useRestoreTableReservationType = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (tableReservationTypeId: string) => {
      return api.restoreTableReservationType({ tableReservationTypeId })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tableReservationTypes'])
      },
    }
  )
}

export const useAddTableReservationOption = (tableReservationTypeId: string) => {
  const queryClient = useQueryClient()

  return useMutation(
    (option: any) =>
      api.createTableReservationOption({
        tableReservationTypeId,
        createTableReservationOptionRequest: option,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tableReservationTypes'])
        queryClient.invalidateQueries(['tableReservationType', { tableReservationTypeId }])
      },
    }
  )
}

export const useRemoveTableReservationOption = (tableReservationTypeId: string) => {
  const queryClient = useQueryClient()

  return useMutation(
    (optionId: string) =>
      api.deleteTableReservationOption({
        tableReservationTypeId,
        optionId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tableReservationTypes'])
        queryClient.invalidateQueries(['tableReservationType', { tableReservationTypeId }])
      },
    }
  )
}
